import {
  Box,
  Button,
  Link as MuiLink,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { isFederalGeneratorDiscrepancy } from "encamp-shared/src/facility/generatorThresholdComparison";
import {
  federalWasteGeneratorStatusToLabel,
  stateWasteGeneratorStatusToLabel,
} from "encamp-shared/src/waste";
import {
  GeneratorStatusExceedanceDetailQuery,
  GeneratorStatusExceedanceDirection,
} from "generated-graphql/graphql";
import { DateTime } from "luxon";
import { useId, useState } from "react";
import { Link } from "react-router-dom";

import { drawerSectionSx, DrawerSectionHeading } from "./DrawerSection";
import { formatLbs, isAcuteDriven } from "./generatorStatusDisplay";
import { formatPeriodMonth, formatPeriodSpan } from "./periodFormat";

type Detail = GeneratorStatusExceedanceDetailQuery["generatorStatusExceedanceDetail"];
type Month = Detail["calculatedMonths"][number];
type RelatedDiscrepancy = Detail["relatedDiscrepancies"][number];
type EvidenceRow = { periodStart: string; month: Month | undefined };

/** Missing calendar months stay explicit so a gap cannot look like a sustained run. */
function evidenceRows(
  months: Month[],
  periodStart: string,
  lastPeriodStart: string
): EvidenceRow[] {
  const byMonth = new Map(months.map((month) => [month.periodStart, month]));
  const end = DateTime.fromISO(lastPeriodStart, { zone: "utc" }).startOf("month");
  const rows: EvidenceRow[] = [];
  for (
    let date = DateTime.fromISO(periodStart, { zone: "utc" }).startOf("month");
    date <= end;
    date = date.plus({ months: 1 })
  ) {
    const period = date.toISODate();
    if (period) rows.push({ periodStart: period, month: byMonth.get(period) });
  }
  return rows;
}

function reportedStatusSourceLabel(month: Month): string {
  if (month.reportedReceiveDate) {
    return `EPA record received ${new Date(month.reportedReceiveDate).toLocaleDateString("en-US", { timeZone: "UTC" })}`;
  }
  if (month.reportedFederalStatus || month.reportedStateStatus) {
    return "Current EPA record; receipt date unavailable";
  }
  return "No reported status available for this month";
}

function EvidenceTableRow({
  row,
  showStateColumns,
}: {
  row: EvidenceRow;
  showStateColumns: boolean;
}) {
  const { month, periodStart } = row;
  if (!month) {
    return (
      <TableRow>
        <TableCell component="th" scope="row">
          {formatPeriodMonth(periodStart)}
        </TableCell>
        <TableCell colSpan={showStateColumns ? 6 : 3} sx={{ color: "text.secondary" }}>
          No calculated data for this month
        </TableCell>
      </TableRow>
    );
  }
  const acute = isAcuteDriven(month.federalStatus, month.acuteLbs, month.totalHazardousLbs);
  return (
    <TableRow>
      <TableCell component="th" scope="row" sx={{ fontWeight: month.inRun ? 600 : 400 }}>
        {formatPeriodMonth(periodStart)}
        {month.inRun && (
          <Box component="span" sx={visuallyHidden}>
            {" "}
            — part of this finding
          </Box>
        )}
      </TableCell>
      <TableCell>
        <Tooltip title={reportedStatusSourceLabel(month)}>
          <span>
            {month.reportedFederalStatus
              ? federalWasteGeneratorStatusToLabel(month.reportedFederalStatus)
              : "—"}
          </span>
        </Tooltip>
      </TableCell>
      <TableCell>
        {month.federalStatus ? federalWasteGeneratorStatusToLabel(month.federalStatus) : "—"}
        {acute && (
          <Typography component="span" variant="caption" sx={{ color: "warning.main", ml: 0.5 }}>
            · acute
          </Typography>
        )}
      </TableCell>
      {showStateColumns && (
        <TableCell>
          {month.reportedStateStatus
            ? stateWasteGeneratorStatusToLabel(month.reportedStateStatus)
            : "—"}
        </TableCell>
      )}
      {showStateColumns && (
        <TableCell>
          {month.stateStatus ? stateWasteGeneratorStatusToLabel(month.stateStatus) : "—"}
        </TableCell>
      )}
      <TableCell align="right">
        {month.totalHazardousLbs != null ? formatLbs(month.totalHazardousLbs) : "—"}
        {month.acuteLbs != null && month.acuteLbs > 0
          ? ` (${formatLbs(month.acuteLbs)} acute)`
          : ""}
      </TableCell>
      {showStateColumns && (
        <TableCell align="right">
          {month.stateRegulatedLbs != null ? formatLbs(month.stateRegulatedLbs) : "—"}
        </TableCell>
      )}
    </TableRow>
  );
}

function comparisonLabel(
  reported: string | null | undefined,
  calculated: string | null | undefined
) {
  const from = reported ? stateWasteGeneratorStatusToLabel(reported) : "None reported";
  const to = calculated ? stateWasteGeneratorStatusToLabel(calculated) : "None calculated";
  return `${from} → ${to}`;
}

function RelatedFindings({
  findings,
  facilityState,
  siblingDrawerSearch,
}: {
  findings: RelatedDiscrepancy[];
  facilityState: string | null;
  siblingDrawerSearch: (periodStart: string) => string;
}) {
  if (findings.length === 0) return null;
  const groups = [
    { direction: GeneratorStatusExceedanceDirection.Upward, label: "Upward exceedances" },
    { direction: GeneratorStatusExceedanceDirection.Downward, label: "Downward trends" },
  ];
  return (
    <Box sx={{ mt: 2 }}>
      <DrawerSectionHeading component="h3">Related findings</DrawerSectionHeading>
      <Stack spacing={1.5}>
        {groups.map(({ direction, label }) => {
          const members = findings.filter((finding) => finding.direction === direction);
          if (members.length === 0) return null;
          return (
            <Box key={direction}>
              <Typography component="h4" variant="body2" sx={{ mb: 0.5 }}>
                {label}
              </Typography>
              <Stack component="ul" spacing={1} sx={{ m: 0, pl: 2 }}>
                {members.map((finding) => (
                  <Box component="li" key={finding.periodStart}>
                    <MuiLink
                      component={Link}
                      to={{ search: siblingDrawerSearch(finding.periodStart) }}
                      variant="body2"
                    >
                      {formatPeriodSpan(finding)}
                    </MuiLink>
                    {isFederalGeneratorDiscrepancy({ ...finding, state: facilityState }) && (
                      <Typography variant="body2" color="text.secondary">
                        Federal:{" "}
                        {comparisonLabel(
                          finding.reportedFederalStatus,
                          finding.calculatedFederalStatus
                        )}
                      </Typography>
                    )}
                    {finding.stateDirection && (
                      <Typography variant="body2" color="text.secondary">
                        {facilityState} state:{" "}
                        {comparisonLabel(
                          finding.reportedStateStatus,
                          finding.calculatedStateStatus
                        )}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Stack>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}

export function CalculatedStatusHistory({
  months,
  relatedDiscrepancies,
  periodStart,
  lastPeriodStart,
  facilityState,
  showStateColumns,
  siblingDrawerSearch,
}: {
  months: Month[];
  relatedDiscrepancies: RelatedDiscrepancy[];
  periodStart: string;
  lastPeriodStart: string;
  facilityState: string | null;
  showStateColumns: boolean;
  siblingDrawerSearch: (periodStart: string) => string;
}) {
  const [showHistory, setShowHistory] = useState(false);
  const tableId = useId();
  const dates = [periodStart, lastPeriodStart, ...months.map((month) => month.periodStart)].sort();
  const first = showHistory ? (dates[0] ?? periodStart) : periodStart;
  const last = showHistory ? (dates.at(-1) ?? lastPeriodStart) : lastPeriodStart;
  const rows = evidenceRows(months, first, last);
  const hasOtherMonths = months.some(
    (month) => month.periodStart < periodStart || month.periodStart > lastPeriodStart
  );
  const title = showHistory ? "Full monthly history" : "Monthly evidence for this finding";
  return (
    <Box sx={drawerSectionSx}>
      <DrawerSectionHeading component="h3">{title}</DrawerSectionHeading>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        {formatPeriodSpan({ periodStart: first, lastPeriodStart: last })}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
        Bold months belong to this finding. Other months provide context. Each month uses its
        reported status at that time; — means unavailable, not zero.
      </Typography>
      <TableContainer id={tableId} tabIndex={0} role="region" aria-label={title}>
        <Table size="small" aria-label={title}>
          <TableHead>
            <TableRow>
              <TableCell>Month</TableCell>
              <TableCell>Reported federal</TableCell>
              <TableCell>Calculated federal</TableCell>
              {showStateColumns && <TableCell>Reported state</TableCell>}
              {showStateColumns && <TableCell>Calculated state</TableCell>}
              <TableCell align="right">Total HW (lbs)</TableCell>
              {showStateColumns && <TableCell align="right">State-reg (lbs)</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <EvidenceTableRow
                key={row.periodStart}
                row={row}
                showStateColumns={showStateColumns}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {hasOtherMonths && (
        <Button
          size="small"
          aria-expanded={showHistory}
          aria-controls={tableId}
          onClick={() => setShowHistory((value) => !value)}
          sx={{ mt: 1 }}
        >
          {showHistory ? "Back to this finding" : "View full monthly history"}
        </Button>
      )}
      <RelatedFindings
        findings={relatedDiscrepancies}
        facilityState={facilityState}
        siblingDrawerSearch={siblingDrawerSearch}
      />
    </Box>
  );
}
